import { createTheme } from '@mui/material/styles';

// USC Official Brand Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#10633C',
      light: '#139B49',
      dark: '#0A4428',
      contrastText: '#FEFEFE',
    },
    secondary: {
      main: '#FACA18',
      light: '#FBD65D',
      dark: '#C9A013',
      contrastText: '#2E2E2E',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#BF9058',
    },
    info: {
      main: '#139B49',
    },
    success: {
      main: '#10633C',
    },
    text: {
      primary: '#2E2E2E',
      secondary: '#424242',
      disabled: '#8A8A8A',
    },
    background: {
      default: '#FEFEFE',
      paper: '#F6F9F5',
    },
    grey: {
      50: '#F6F9F5',
      100: '#E8EDE8',
      200: '#D1DBD1',
      300: '#8A8A8A',
      400: '#424242',
      500: '#2E2E2E',
      600: '#000000',
    },
  },

  typography: {
    htmlFontSize: 18,
    fontSize: 18,
    fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontFamily: '"Source Sans 3", sans-serif',
      fontSize: '3.815rem',
      fontWeight: 900,
      lineHeight: 0.8,
      letterSpacing: '-2px',
      color: '#2E2E2E',
      '@media (max-width:900px)': {
        fontSize: '2.5rem',
        lineHeight: 1,
      },
      '@media (max-width:600px)': {
        fontSize: '2rem',
        lineHeight: 1.1,
      },
    },
    h2: {
      fontFamily: '"Source Sans 3", sans-serif',
      fontSize: '2.441rem',
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: '1px',
      color: '#2E2E2E',
      '@media (max-width:900px)': {
        fontSize: '2rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '1.563rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
      color: '#2E2E2E',
      '@media (max-width:900px)': {
        fontSize: '1.4rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h4: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: '0px',
      color: '#2E2E2E',
    },
    h5: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '1.1rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
      color: '#2E2E2E',
    },
    h6: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '0.9rem',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.2px',
      textTransform: 'uppercase',
      color: '#2E2E2E',
    },
    body1: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0px',
      color: '#2E2E2E',
    },
    body2: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '0.8rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0px',
      color: '#424242',
    },
    subtitle1: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0px',
      color: '#2E2E2E',
    },
    subtitle2: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '0.9rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0px',
      color: '#424242',
    },
    caption: {
      fontFamily: '"Source Sans 3", sans-serif',
      fontSize: '0.64rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: '#424242',
    },
    button: {
      fontFamily: '"Public Sans", sans-serif',
      fontSize: '0.9rem',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.2px',
      textTransform: 'uppercase',
    },
    overline: {
      fontFamily: '"Source Sans 3", sans-serif',
      fontSize: '0.64rem',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      lineHeight: 1.3,
    },
  },

  shape: {
    borderRadius: 0,  // CHANGED: No curves!
  },

  spacing: 8,

  components: {
    // 🔥 CRITICAL FIX: Global baseline styles to remove white space
    MuiCssBaseline: {
      styleOverrides: `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
        #root {
          margin: 0 !important;
          padding: 0 !important;
        }
      `,
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,  // CHANGED: No curves
          padding: '12px 24px',
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '-0.2px',
          textTransform: 'uppercase',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          backgroundColor: '#10633C',
          color: '#FEFEFE',
          '&:hover': {
            backgroundColor: '#0A4428',
          },
        },
        containedSecondary: {
          backgroundColor: '#FACA18',
          color: '#2E2E2E',
          '&:hover': {
            backgroundColor: '#C9A013',
          },
        },
        outlined: {
          borderColor: '#10633C',
          color: '#10633C',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(16, 99, 60, 0.04)',
          },
        },
        text: {
          color: '#10633C',
          '&:hover': {
            backgroundColor: 'rgba(16, 99, 60, 0.04)',
          },
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1.1rem',
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.64rem',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,  // CHANGED: No curves
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          backgroundColor: '#F6F9F5',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,  // CHANGED: No curves
            backgroundColor: '#FEFEFE',
            '& fieldset': {
              borderColor: '#8A8A8A',
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: '#10633C',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#10633C',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            fontWeight: 600,
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#10633C',
          color: '#FEFEFE',
          boxShadow: 'none',  // CHANGED: Removed shadow
          margin: 0,  // 🔥 CRITICAL: No margin
          padding: 0,  // 🔥 CRITICAL: No padding
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          margin: 0,  // 🔥 CRITICAL: No margin
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,  // CHANGED: No curves
          fontWeight: 700,
          fontSize: '0.8rem',
        },
        filled: {
          backgroundColor: '#139B49',
          color: '#FEFEFE',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,  // CHANGED: No curves
          fontSize: '0.9rem',
        },
        standardSuccess: {
          backgroundColor: '#E8F5E9',
          color: '#10633C',
        },
        standardError: {
          backgroundColor: '#FFEBEE',
          color: '#C62828',
        },
        standardWarning: {
          backgroundColor: '#FFF3E0',
          color: '#BF9058',
        },
        standardInfo: {
          backgroundColor: '#E3F2FD',
          color: '#139B49',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,  // CHANGED: No curves
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#10633C',
          color: '#FEFEFE',
          fontWeight: 700,
          fontSize: '0.9rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },
        body: {
          fontSize: '0.9rem',
        },
      },
    },
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default theme;